import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useDecommissionProject } from '@/hooks'
import type { Project } from '@/types'
import { Power, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

interface DecommissionProjectDialogProps {
  project: Project
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DecommissionProjectDialog({ project, open, onOpenChange }: DecommissionProjectDialogProps) {
  const [confirmationText, setConfirmationText] = useState('')
  const decommissionProject = useDecommissionProject()
  const navigate = useNavigate()

  const isConfirmationValid = confirmationText === project.name

  useEffect(() => {
    if (!open) {
      setConfirmationText('')
    }
  }, [open])

  const handleDecommission = async () => {
    if (!isConfirmationValid) return

    try {
      await decommissionProject.mutateAsync(project.id)
      toast.success('Project decommissioned successfully')
      onOpenChange(false)
      navigate('/projects')
    } catch {
      toast.error('Failed to decommission project')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated"
        hideCloseButton
      >
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
            Decommission Project
          </DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          Decommissioning permanently deletes "<span className="font-bold">{project.name}</span>" and
          all its work items, intents, prompts, workflows, chats, and test artifacts. This action
          cannot be undone.
        </DialogDescription>
        <div className="space-y-2">
          <Input
            placeholder="Enter project name"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            className="w-full"
            aria-label="Type project name to confirm decommission"
            data-testid="decommission-confirmation-input"
          />
          <p className="text-sm text-muted-foreground">
            Type "{project.name}" to enable the Decommission Project button.
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDecommission}
            disabled={!isConfirmationValid || decommissionProject.isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="confirm-decommission-button"
          >
            <Power className="h-5 w-5" />
            {decommissionProject.isPending ? 'Decommissioning...' : 'Decommission Project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
